// jshint strict:false, undef:false, unused:false

/**
 * I4E Enrollment Loader v2
 * Refactored for modern JavaScript (ES6+)
 * Updated 2025
 */

class SessionManager {
	constructor(system) {
		this.system = system;
	}
	
	async saveData(enrollment) {
		try {
			const json = await this.system._fetchJSON("/savesession.php", {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(enrollment)
			});
			return json;
		} catch (err) {
			console.error("Error saving data:", err);
			throw err;
		}
	}
	
	async getData() {
		try {
			const json = await this.system._fetchJSON("/getsession.php");
			this.system.enrollment = json;
			if (this.system && this.system.updateUI) {
				this.system.updateUI();
			}
			return json;
		} catch (err) {
			console.error("Error getting data:", err);
			throw err;
		}
	}
	
	async newSession() {
		try {
			const json = await this.system._fetchJSON("/newsession.php");
			this.system.enrollment = json;
			if (this.system && this.system.updateUI) {
				this.system.updateUI();
			}
			return json;
		} catch (err) {
			console.error("Error creating new session:", err);
			throw err;
		}
	}
}

class EnrollmentSystem {
	constructor() {
		this.currencyFormatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		});
		
		this._enrollment = window.enrollment || {};
		this.configPromise = this.loadConfig();
		
		this.sessionManager = new SessionManager(this);
		
		// Bind methods
		this.saveData = this.sessionManager.saveData.bind(this.sessionManager);
		this.getData = this.sessionManager.getData.bind(this.sessionManager);
		this.newSession = this.sessionManager.newSession.bind(this.sessionManager);
		this.updateUI = this.updateUI.bind(this);
	}
	
	get enrollment() {
		return this._enrollment;
	}
	
	set enrollment(value) {
		this._enrollment = value;
		window.enrollment = value; // Fallback
	}
	
	async _fetchJSON(url, options = {}) {
		const res = await fetch(url, options);
		if (!res.ok) throw new Error(`HTTP Error ${res.status}: ${res.statusText}`);
		return await res.json();
	}
	
	async loadConfig() {
		try {
			const json = await this._fetchJSON("/data/enrollment-config.json");
			this.defaults = json.defaults;
			this.settings = json.config;
			this.urls = this.settings; // Alias for compatibility
			return json;
		} catch (err) {
			console.error("Error loading config:", err);
		}
	}
	
	formatMoney(amount) {
		const num = parseFloat(amount);
		
		// Return a fallback if it's not a number
		if (isNaN(num)) return amount || '$0.00';
		
		// Convert cents (Integer) to dollars (Decimal)
		return this.currencyFormatter.format(num / 100);
	}
	
	async fetchCarriers() {
		await this.configPromise;
		const portalElement = document.getElementById("data-portal");
		if (!portalElement) return [];
		
		const portal = JSON.parse(portalElement.textContent);
		
		try {
			const json = await this._fetchJSON(this.urls.carriers);
			return json.filter(c => portal.types.includes(c.type));
		} catch (err) {
			console.error("Error getting carriers:", err);
			throw err;
		}
	}
	
	async fetchPlans() {
		await this.configPromise;
		try {
			const [plans, carriers] = await Promise.all([
				this._fetchJSON(this.urls.plans),
				this._fetchJSON(this.urls.carriers)
			]);
			
			plans.forEach(plan => {
				// Match Plans With Carriers
				const match = carriers.find(({ name }) => name === plan.carrier) || false;
				plan.logo = match.src || "https://i4e.cremadesignstudio.com/assets/img/logo-coming-soon.svg";

				plan.layout = {
					"benefits": "",
					"expiry": ""
				};

				plan.links = {
					"modal": "#modal-details"
				};
			});
			
			return plans;
		} catch (err) {
			console.error("Error loading plans:", err);
			throw err;
		}
	}
	
	async fetchLocationByZip(zip) {
		// Default to 39110 if no ZIP provided
		const zipCode = (zip && String(zip).trim()) ? String(zip).trim() : '39110';
		const url = `https://ziptasticapi.com/${zipCode}`;
		
		console.log("Fetching URL: " + url);
		
		try {
			const json = await this._fetchJSON(url);
			
			const city = json.city || '';
			if (city) {
				json.city = city.charAt(0) + city.toLowerCase().slice(1);
			}
			
			return json;
		} catch (err) {
			console.error("Error getting city/state:", err);
			throw err;
		}
	}
	
	generateActionUrls(plan) {
		const member = this.enrollment.member;
		const start = encodeURIComponent(member.start);
		const dob = encodeURIComponent(member.dob);

		return {
			"details": `/Products/DetailsPartial?GroupProductId=${plan.id}&Zip=${member.zip}&Portal=${this.enrollment.portal}&EffDate=${start}&Birthdate=${dob}`,
			"share": `/Products/Share?GroupProductId=${plan.id}`,
			"enroll": `/Enroll/Participant?GroupProductId=${plan.id}&Zip=${member.zip}&Portal=${this.enrollment.portal}&EffDate=${start}&DOB=${dob}`
		};
	}
	
	renderComparisonPopover(plan) {
		let template = '';
		
		this.enrollment.compare[plan.type].forEach(p => {
			template += `<div class="mb-2">
				<div class="btn-group mb-0">
					<div class="btn btn-xs btn-dark btn-remove">
						<span class="fa-2x">&times;</span>
					</div>

					<div class="btn btn-xs btn-light active nohover text-left">${p}</div>
				</div>
			</div>`;
		});
		
		return `${template}
		<hr class="dashed my-2" />
		
		<div class="pt-3 mb-2 popover-actions">
			<a class="btn btn-pill btn-outline-success btn-compare" href="/compare?type=${plan.type}">Compare</a>
			<a class="btn btn-link text-success btn-clear" href="#">Clear All</a>
		</div>`;
	}
	
	removeCompareItem(label, planType) {	
		const title = label.querySelector(".btn-light").textContent;
		const i = this.enrollment.compare[planType].findIndex(plan => plan === title);
		
		if (i > -1) {
			this.enrollment.compare[planType].splice(i, 1);
			label.remove();

			// Uncheck the matching plan card's compare checkbox
			document.querySelectorAll('.plan').forEach(card => {
				const input = card.querySelector('[name="plan"]');
				if (input && JSON.parse(input.value).title === title) {
					const checkbox = card.querySelector('[data-toggle="popover"]');
					if (checkbox) checkbox.checked = false;
				}
			});

			this.saveData(this.enrollment);
		}
	}
	
	renderPlanCard(plan) {
		const portalElement = document.getElementById("data-portal");
		const portal = portalElement ? JSON.parse(portalElement.textContent) : { id: '' };
		
		const planData = JSON.stringify({
			id: plan.id,
			type: plan.type,
			title: plan.title,
			price: plan.price,
			coverage: ["Primary", "Spouse"]
		});
		
		let ribbon = '';
		if (plan.title.includes("Vision")) {
			ribbon = '<div class="badge-attributes badge-info attribute attribute-packaged" title="Available with packaged&nbsp;vision" data-toggle="tooltip" data-placement="top"><i class="fas fa-eye"></i></div>';
		}
		
		let checked = '';
		if (this.enrollment.compare[plan.type]) {
			checked = (this.enrollment.compare[plan.type].includes(plan.title) ? 'checked' : '');
		}
		
		let benefitsTable = '';
		let featuredClass = '';
		
		if (portal.id == 'DFE') {
			benefitsTable = `<table class="table table-bordered bg-light table-sm text-center">
				<tr>
					<th width="50%" scope="row">Waiting Period</th>
					<td width="50%">Yes</td>
				</tr>
				<tr>
					<th scope="row">Deductible</th>
					<td>$100/$150</td>
				</tr>
				<tr>
					<th scope="row">Annual Max</th>
					<td>$1500</td>
				</tr>
			</table>`;
			
			featuredClass = (plan.featured) ? ' border border-2 border-primary' : '';
		}
		
		return `<div class="plan-wrap plan-wrap-${plan.type}" data-price="${plan.price}" data-featured="${plan.featured ? 1 : 0}">
			<div class="plan rounded-5 card-${plan.type}${featuredClass}">
				${ribbon}
				
				<div class="logo-wrapper">
					<img src="${plan.logo}" class="logo">
				</div>
				
				<input type="hidden" name="plan" value='${planData}'>
				
				<div class="details">
					<h3 class="title"><a data-toggle="modal" href="${plan.links.modal}">${plan.title}</a></h3>
					<p class="subtitle">${plan.description}</p>
					
					${plan.layout.benefits}
					
					<div class="price">${this.formatMoney(plan.price)}</div>
					
					${benefitsTable}
					
					<div class="actions">
						<a class="btn-buy" data-toggle="modal" href="#modal-add-cart"><i class="icon"></i> Buy Now</a>
						
						${plan.layout.expiry}
						
						<div class="btn-group">
							<a class="btn-details" data-toggle="modal" href="${plan.links.modal}">View Details</a>
							<a class="btn-doctor" href="${plan.provider_link}" target="_blank">Find Doctor</a>
						</div>
					</div>
				</div>
				
				<label class="lbl-compare">
					<div class="custom-control custom-checkbox">
						<input type="checkbox" class="custom-control-input" data-toggle="popover" title="Selected Items (Max of 5)" ${checked}>
						<div class="custom-control-label">Add to Compare</div>
					</div>
				</label>
			</div>
		</div>`;
	}
	
	renderPlanCards(plans) { 
		plans.forEach(plan => {
			const target = document.querySelector(`#tab-${plan.type} .results`);
			if (target) target.insertAdjacentHTML('beforeend', this.renderPlanCard(plan));
		});
		
		// Add Click Events to Plan Cards
		this.addPlanClickEvents();
		
		// Initialize Tooltips for dynamically added elements
		if (typeof $ !== 'undefined' && $.fn.tooltip) {
			$('[data-toggle="tooltip"]').tooltip();
		}
		
		// Initialize Popovers and add Click Events
		const triggers = document.querySelectorAll('[data-toggle="popover"]');
		
		triggers.forEach(trigger => trigger.addEventListener("click", e => {		
			const plan = JSON.parse(trigger.closest(".plan").querySelector('[name="plan"]').value);
			
			// Normalize compare to an object; PHP serializes empty {} as [],
			// and keyed writes on an array are dropped by JSON.stringify.
			if (Array.isArray(this.enrollment.compare)) {
				this.enrollment.compare = {};
			}

			// Add or Remove Comparison Items, Based on Checkbox Value
			if (trigger.checked) {
				if (!this.enrollment.compare.hasOwnProperty(plan.type)) {
					this.enrollment.compare[plan.type] = [];
				}
				
				if (this.enrollment.compare[plan.type].includes(plan.title) == false && this.enrollment.compare[plan.type].length < 5) {
					this.enrollment.compare[plan.type].push(plan.title);
				}
			} else if (Array.isArray(this.enrollment.compare[plan.type])) {
				this.enrollment.compare[plan.type] = this.enrollment.compare[plan.type].filter(value => value != plan.title);
			}
			
			this.saveData(this.enrollment);
			
			if (typeof $ !== 'undefined' && $.fn.popover) {
				// Close any Selected Items popover already open on another card.
				// Only target triggers with an open popover (aria-describedby),
				// so we don't lazily init content-less popovers elsewhere.
				$('[data-toggle="popover"][aria-describedby]').not(trigger).popover('hide');

				$(trigger).popover({
					html: true,
					content: this.renderComparisonPopover(plan)
				});
				
				$(trigger).popover('show').on('shown.bs.popover', (e) => {
					const popoverId = trigger.getAttribute("aria-describedby");
					const $popover = $('#' + popoverId);
					const $remove = $popover.find('.btn-remove');
					const $clear = $popover.find('.btn-clear');
					
					this.updateUI();
					
					$remove.on('click', (e) => {
						e.stopImmediatePropagation();

						const group = e.currentTarget.closest('.btn-group');
						const removedTitle = group.querySelector('.btn-light').textContent;

						this.removeCompareItem(group, plan.type);
						this.updateUI();

						// Removing this card's own item closes the popover; others stay open
						if (removedTitle === plan.title) {
							$(trigger).popover('hide');
						}
					});
					
					$clear.on('click', (e) => {
						e.preventDefault();
						e.stopImmediatePropagation();
						
						// Uncheck all the comparison checkboxes
						const checkboxes = document.querySelectorAll('[data-toggle="popover"]');
						checkboxes.forEach(checkbox => checkbox.checked = false);
						this.enrollment.compare = {};
						
						this.saveData(this.enrollment);

						// Hide Popover
						$popover.popover('hide');
					});
				});
			}
		}));

		// Wire sorting and the results summary now that cards exist
		this.initSort();
		this.updateResultsSummary();
	}

	// Reorder the .plan-wrap cards within a results container
	sortPlanCards(resultsEl, mode) {
		const cards = Array.from(resultsEl.querySelectorAll('.plan-wrap'));
		const price = el => parseFloat(el.getAttribute('data-price')) || 0;
		const featured = el => parseInt(el.getAttribute('data-featured'), 10) || 0;

		cards.sort((a, b) => {
			if (mode === 'price-asc') return price(a) - price(b);
			if (mode === 'price-desc') return price(b) - price(a);
			// 'featured': featured first, then lowest price
			return featured(b) - featured(a) || price(a) - price(b);
		});

		// Re-appending a live node moves it, so this reorders in place
		cards.forEach(card => resultsEl.appendChild(card));
	}

	// Wire each tab's sort dropdown and apply its default sort
	initSort() {
		document.querySelectorAll('.tab-pane').forEach(pane => {
			const results = pane.querySelector('.results');
			const items = pane.querySelectorAll('[data-sort]');
			if (!results || !items.length) return;

			const dropdown = items[0].closest('.dropdown');
			const toggle = dropdown ? dropdown.querySelector('.dropdown-toggle') : null;

			items.forEach(item => {
				item.addEventListener('click', e => {
					e.preventDefault();
					items.forEach(i => i.classList.remove('active'));
					item.classList.add('active');
					if (toggle) toggle.textContent = `Sorted by ${item.textContent.trim()}`;
					this.sortPlanCards(results, item.getAttribute('data-sort'));
				});
			});

			const active = pane.querySelector('[data-sort].active') || items[0];
			this.sortPlanCards(results, active.getAttribute('data-sort'));
		});
	}

	// Populate "N results found in City, ST" per tab
	updateResultsSummary() {
		const member = this.enrollment.member || {};
		const location = [member.city, member.state].filter(Boolean).join(', ');

		document.querySelectorAll('.tab-pane').forEach(pane => {
			const count = pane.querySelectorAll('.results .plan-wrap').length;
			const countEl = pane.querySelector('.results-count');
			const locEl = pane.querySelector('.results-location');
			if (countEl) countEl.textContent = count;
			if (locEl) locEl.textContent = location;
		});
	}

	createComparisonCards(plans) {
		const target = document.querySelector("#compare-plans");
		if (!target) return;
		
		const compareTitles = Array.isArray(this.enrollment.compare) ? this.enrollment.compare : Object.values(this.enrollment.compare).flat();
		
		compareTitles.forEach(thisTitle => {
			const plan = plans.find(({ title }) => title === thisTitle);
			if (!plan) return;
			
			const planData = JSON.stringify({
				id: plan.id,
				type: plan.type,
				title: plan.title,
				price: plan.price,
				coverage: ["Primary", "Spouse"]
			});
			
			let template = '';
			
			switch(plan.type) {
				case 'dental':
				case 'vision':
					let rows = '';
					
					if (plan.benefits && plan.benefits.rows) {
						plan.benefits.rows.forEach(benefit => {
							rows += `<tr>
								<th scope="row" class="benefit">${benefit.name}</th>
								<td class="text-left waiting">${benefit.waiting}</td>
								<td class="amount">${benefit.cost}</td>
							</tr>`;
						});
					}
					
					const benefitsTable = `<table class="table-sm table-benefits">${rows}</table>`;
					const deductible = plan.deductible ? plan.deductible.split('*')[0].split('.')[0] : '';
					const maximum = plan.maximum ? plan.maximum.split('/')[0] : '';
					
					template = `<div class="col">
						<div class="plan">
							<input type="hidden" name="plan" value='${planData}'>
							
							<div class="card-body">
								<h3 class="mb-0 title"><a data-toggle="modal" href="#modal-details">${plan.title}</a></h3>
								<p class="lead mb-2">${plan.description}</p>
								<div class="h3 mt-0">${this.formatMoney(plan.price)}</div>

								<div class="d-flex justify-content-center">
									<a class="btn btn-sm" data-toggle="modal" href="#modal-details">View Details</a>
									<a class="btn btn-sm" href="${plan.provider_link}" target="_blank">Find Doctor</a>
								</div>
							</div>
							
							<div class="details p-0">
								<ul class="list-group list-group-flush">
									<li class="list-group-item">
										<h4 class="mb-0">Waiting / Costs</h4>
										<p class="mb-0">${benefitsTable}</p>
									</li>
									
									<li class="list-group-item">
										<h4 class="mb-0">Deductible</h4>
										<p class="mb-0">${deductible}</p>
									</li>
									
									<li class="list-group-item">
										<h4 class="mb-0">Office Copay</h4>
										<p class="mb-0">${plan.details ? plan.details.copay : ''}</p>
									</li>
									
									<li class="list-group-item">
										<h4 class="mb-0">Plan Maximum</h4>
										<p class="mb-0">${maximum}</p>
									</li>
									
									<li class="list-group-item pb-0">
										<a class="btn-buy" data-toggle="modal" href="#modal-add-cart"><i class="icon"></i> Buy Now</a>
										
										<span class="expiry">
											<i class="icon"></i> 2 days left to enroll
										</span>
									</li>
								</ul>
							</div><!--/card-footer-->
						</div><!--/card-->
					</div><!--/col-->`;
				break;
				case 'medical':
					let listitems = ``;
					
					if (plan.benefits) {
						for (const [benefit, value] of Object.entries(plan.benefits)) {
							let description = ``;
							
							for (let [subbenefit, subvalue] of Object.entries(value)) {
								if (typeof subvalue == 'number') subvalue = this.formatMoney(subvalue).replace(/\.00$/, '');
								description += `<p class="mb-0">${subbenefit}: ${subvalue}</p>`;
							}
							
							listitems += `<li class="list-group-item">
								<h4 class="mb-0">${benefit}</h4>
								${description}
							</li>`;
						}
					}
					
					template = `<div class="col">
						<div class="plan">
							<input type="hidden" name="plan" value='${planData}'>
							
							<div class="card-body">
								<h3 class="mb-0 title"><a data-toggle="modal" href="#modal-details">${plan.title}</a></h3>
								<p class="lead mb-2">${plan.description}</p>
								<div class="h3 mt-0">${this.formatMoney(plan.price)}</div>
								
								<div class="d-flex justify-content-center">
									<a class="btn btn-sm" data-toggle="modal" href="#modal-details">View Details</a>
								</div>
							</div>
							
							<div class="details p-0">
								<ul class="list-group list-group-flush">
									${listitems}
								</ul>
							</div><!--/card-footer-->
						</div><!--/card-->
					</div><!--/col-->`;
				break;
				default: // identity, telemed, etc.
					template = `<div class="col">
						<div class="plan">
							<input type="hidden" name="plan" value='${planData}'>
							
							<div class="card-body">
								<h3 class="mb-0 title"><a data-toggle="modal" href="#modal-details">${plan.title}</a></h3>
								<p class="lead mb-2">${plan.description}</p>
								<div class="h3 mt-0">${this.formatMoney(plan.price)}</div>
								
								<div class="d-flex justify-content-center">
									<a class="btn btn-sm" data-toggle="modal" href="#modal-details">View Details</a>
								</div>
							</div>
							
							<div class="details p-0">
								<ul class="list-group list-group-flush">
									<li class="list-group-item">
										<h4 class="mb-0">Field</h4>
										<p class="mb-0">Value</p>
									</li>
								</ul>
							</div><!--/card-footer-->
						</div><!--/card-->
					</div><!--/col-->`;
				break;
			}
			
			// Insert Plan Card
			target.insertAdjacentHTML('beforeend', template);
		});
		
		// Add Click Events to Plan Cards
		this.addPlanClickEvents();
	}
	
	async loadComparisonCards(selector) {	
		try {
			const res = await fetch("/layouts/comparison-cards" + location.search);
			const html = await res.text();
			
			// Insert Plan Cards
			const target = document.querySelector(selector);
			if (target) target.insertAdjacentHTML('beforeend', html);
			
			// Add Click Events to Plan Cards
			this.addPlanClickEvents();
			
			const arrows = {
				"left": '<svg height="62" width="32"><line x1="0" y1="31" x2="30" y2="2" style="stroke:currentColor;stroke-width:3" /><line x1="0" y1="29" x2="30" y2="58" style="stroke:currentColor;stroke-width:3" /></svg>',
				"right": '<svg height="62" width="32"><line x1="30" y1="31" x2="0" y2="2" style="stroke:currentColor;stroke-width:3" /><line x1="30" y1="29" x2="0" y2="58" style="stroke:currentColor;stroke-width:3" /></svg>'
			};
			
			if (typeof $ !== 'undefined' && $.fn.slick) {
				$('.slider-comparison').slick({
					fade: false,
					infinite: false,
					speed: 750,
					autoplay: false,
					prevArrow: `<a class="carousel-control left slick-prev pl-0">${arrows.left}</a>`,
					nextArrow: `<a class="carousel-control right slick-next pr-0">${arrows.right}</a>`,
					slidesToShow: 3,
					slidesToScroll: 3,
					responsive: [
						{
							breakpoint: 320,
							settings: {
								swipeToSlide: true,
								slidesToShow: 1,
								slidesToScroll: 1
							}
						},
						{
							breakpoint: 768,
							settings: {
								slidesToShow: 1,
								slidesToScroll: 1
							}
						},
						{
							breakpoint: 992,
							settings: {
								swipeToSlide: false,
								swipe: false,
								slidesToShow: 2,
								slidesToScroll: 2,
							}
						},
						{
							breakpoint: 1200,
							settings: {
								swipeToSlide: false,
								swipe: false,
								slidesToShow: 3,
								slidesToScroll: 3,
							}
						}
					]
				});
			}
			
			return html;
		} catch (err) {
			console.error("Error loading comparison cards:", err);
			throw err;
		}
	}
	
	renderDependentButtons(enrollment) {
		let html = '';
		
		if (enrollment.dependents.length) {
			enrollment.dependents.forEach(dependent => {
				html += `<div class="btn-group mb-1">
					<span class="btn btn-sm btn-outline-secondary border-right-0 nohover">${dependent.name_first} ${dependent.name_last}</span>
					<a class="btn btn-sm btn-outline-secondary border-left-0"><i class="fa fa-times"></i></a>
				</div> `;
				
				html += `<div class="btn-group mr-2 mb-1">
					<span class="btn btn-xs btn-secondary nohover w-auto">Plan Three Title</span>
					<a class="btn btn-xs btn-secondary w-auto"><i class="fa fa-times"></i></a>
				</div>`;
			});
			
			html = `<label class="w-100">Dependents</label><div class="mb-3">${html}</div>`;
		}
		
		return html;
	}
	
	getProductButtons(enrollment) {
		let html = '';
		
		if (enrollment.plans.length) {
			enrollment.plans.forEach(plan => {
				html += `<div class="btn-group mr-2 mb-1">
					<span class="btn btn-xs btn-secondary nohover w-auto">${plan.title}</span>
					<a class="btn btn-xs btn-secondary w-auto"><i class="fa fa-times"></i></a>
				</div>`;
			});
		}
		
		return html;
	}
	
	getProductCheckboxes(enrollment) {
		let html = '';
		
		if (enrollment.plans.length) {
			enrollment.plans.forEach(plan => {
				html += `<label class="custom-control custom-checkbox d-flex">
					<input type="checkbox" class="custom-control-input" checked>
					<span class="custom-control-label">${plan.title}</span>
				</label>`;
			});
			
			html = `<h4>Enrolled Products</h4>${html}`;
		}
		
		return html;
	}

	addDependent(e) {
		e.preventDefault();
		
		const form = e.target;
		const fields = serializeJSON(form); // Assuming serializeJSON is globally available
		let template = '';
		let target = null;
		
		const dependent = {
			"id": Date.now(),
			"name_first": fields.name_first,
			"name_last": fields.name_last,
			"dob": fields.dob,
			"gender": fields.gender,
			"relationship": fields.relationship
		};
		
		window.enrollment.dependents.push(dependent);
		
		this.saveData(window.enrollment);
		this.updateUI();
		
		if (e.target.querySelector("table")) {
			target = document.querySelector("#tbl-dependents tbody");
			
			template = `<tr data-id="${dependent.id}">
				<th scope="row" class="pl-0">${dependent.name_first} ${dependent.name_last}</th>
				<td>${dependent.dob}</td>
				<td>${dependent.gender}</td>
				<td>${dependent.relationship}</td>
				<td align="right" class="pr-0"><a data-toggle="modal" href="#modal-dependent-edit"><i class="fa fa-pencil"></i> Edit</a></td>
			</tr>`;
		} else {
			target = document.querySelector("#formDependents .list-group");
			
			template = `<li class="list-group-item dependent" data-id="${ dependent.id }">
				<div class="row">
					<div class="col-md-4 font-weight-bold name">
						${dependent.name_first} ${dependent.name_last}
					</div>
					
					<div class="col-9 col-md-4 details">
						${dependent.dob}, ${dependent.gender}, ${dependent.relationship}
					</div>
					
					<div class="col-3 col-md-4 text-right actions">
						<a data-toggle="modal" href="#modal-dependent-edit"><i class="fa fa-pencil"></i> Edit</a>
					</div>
				</div>
				
				<label class="small text-muted mb-0">Enrolled Products</label>
				<div class="input-group input-group-sm">
					<div class="form-control d-flex flex-wrap border-right-0 h-auto pb-0">
						${this.getProductButtons(window.enrollment)}
					</div>
					
					<div class="input-group-append">
						<a class="btn btn-primary d-flex align-items-center" data-toggle="modal" href="#modal-dependent-edit"><i class="fa fa-plus"></i></a>
					</div>
				</div>
			</li>`;
		}
		
		if (target) target.insertAdjacentHTML('beforeend', template);
		
		const submitBtn = document.querySelector('.nav-actions .btn-continue');
		if (submitBtn) {
			submitBtn.removeAttribute("disabled");
			submitBtn.classList.remove("disabled");
		}
		
		form.reset();
		const submitInput = form.querySelector('[type=submit]');
		if (submitInput) submitInput.blur();
		
		const spouseCard = document.querySelector(".page .card-spouse");
		if (spouseCard) spouseCard.classList.add("d-none");
		
		const noDependentsAlert = document.querySelector(".alert-no-dependents");
		if (noDependentsAlert) noDependentsAlert.classList.add("d-none");
		
		if (typeof $ !== 'undefined' && $.fn.modal) {
			$('.modal').modal('hide');
		}
	}
	
	addRider(e) {
		e.preventDefault();
		
		const target = document.querySelector(".enrollment-summary .line-items");
		const card = e.target.closest(".plan");
		
		if (!card) return;
		
		const addon = {
			"title": card.querySelector(".title").textContent,
			"subtitle": card.querySelector(".subtitle").textContent,
			"price": card.querySelector(".price").textContent.split('/')[0]
		};
		
		const template = `
		<li class="list-group-item">
			<div class="d-flex justify-content-between">
				<div class="item">${addon.title}</div>
				<div class="item">${addon.price}</div>
			</div>
		</li>`;
		
		if (target) target.insertAdjacentHTML('beforeend', template);
		
		const rider = card.closest(".rider");
		if (rider) rider.remove();
	}

	updateCart() {
		const cartCount = window.enrollment.plans.length || 0;
		
		if (cartCount > 0) {
			// Update cart quantities and animate cart icon
			document.body.setAttribute("data-plans", cartCount);
			
			// Animate cart icon
			const cartLinks = document.querySelectorAll(".navbar .link-cart");
			cartLinks.forEach(el => {
				const counts = el.querySelectorAll(".cart-count");
				counts.forEach(count => count.textContent = cartCount);
				
				el.classList.remove('animate__animated', 'animate__tada');
				// Force reflow to restart animation
				void el.offsetWidth;
				el.classList.add('animate__animated', 'animate__tada');
			});
		}
	}
	
	updateUI(element=document, scope=window) {
		element.querySelectorAll('[data-ref]').forEach(el => {
			const ref = el.getAttribute("data-ref");
			try {
				el.textContent = ref.split('.').reduce((obj, prop) => obj[prop], scope);
			} catch (e) {
				console.warn(`Could not resolve reference: ${ref}`);
			}
		});
		
		this.updateCart();
	}
	
	editStartDate(btn) {
		const listItem = btn.closest(".start-date");
		window.enrollment.member.start = listItem.querySelector('[name="start"]').value;
		this.saveData(window.enrollment);
		
		this.getData();
		this.updateUI();
	}
	
	updateSearch(e) {
		e.preventDefault();
		
		window.enrollment.member = serializeJSON(e.target);
		
		this.fetchLocationByZip(window.enrollment.member.zip).then(locale => {
			window.enrollment.member.city = locale.city;
			window.enrollment.member.state = locale.state;
			this.saveData(window.enrollment);
			this.updateUI();
			e.target.submit();
		});
	}
	
	addPlanClickEvents() {
		const buttons = document.querySelectorAll('[href="#modal-details"], [href="#modal-edit-plan"], [href="#modal-add-cart"]');
		buttons.forEach(btn => {
			btn.addEventListener("click", e => {
				const planInput = btn.closest(".plan, .cart-item").querySelector('[name="plan"]');
				if (planInput) {
					sessionStorage.setItem("plan", planInput.value);
				}
			});
		});
	}
	
	handleSubmit(e) {
		e.preventDefault();
		
		this.saveData(window.enrollment).then(msg => {
			this.updateUI();
			e.target.submit();
		});
	}
}

// Initialize the system
const enrollmentSystem = new EnrollmentSystem();

// Expose functions globally if needed for legacy inline calls
window.saveData = enrollmentSystem.saveData.bind(enrollmentSystem);
window.getData = enrollmentSystem.getData.bind(enrollmentSystem);
window.newSession = enrollmentSystem.newSession.bind(enrollmentSystem);
window.getCarriers = enrollmentSystem.fetchCarriers.bind(enrollmentSystem);
window.loadPlans = enrollmentSystem.fetchPlans.bind(enrollmentSystem);
window.getCityState = enrollmentSystem.fetchLocationByZip.bind(enrollmentSystem);
window.getActionUrls = enrollmentSystem.generateActionUrls.bind(enrollmentSystem);
window.loadComparisonPopover = enrollmentSystem.renderComparisonPopover.bind(enrollmentSystem);
window.removeCompareItem = enrollmentSystem.removeCompareItem.bind(enrollmentSystem);
window.getPlanCard = enrollmentSystem.renderPlanCard.bind(enrollmentSystem);
window.createPlanCards = enrollmentSystem.renderPlanCards.bind(enrollmentSystem);
window.createComparisonCards = enrollmentSystem.createComparisonCards.bind(enrollmentSystem);
window.loadComparisonCards = enrollmentSystem.loadComparisonCards.bind(enrollmentSystem);
window.getDependentButtons = enrollmentSystem.renderDependentButtons.bind(enrollmentSystem);
window.getProductButtons = enrollmentSystem.getProductButtons.bind(enrollmentSystem);
window.getProductCheckboxes = enrollmentSystem.getProductCheckboxes.bind(enrollmentSystem);
window.addDependent = enrollmentSystem.addDependent.bind(enrollmentSystem);
window.addRider = enrollmentSystem.addRider.bind(enrollmentSystem);
window.updateCart = enrollmentSystem.updateCart.bind(enrollmentSystem);
window.refreshDOM = enrollmentSystem.updateUI.bind(enrollmentSystem);
window.editStartDate = enrollmentSystem.editStartDate.bind(enrollmentSystem);
window.updateSearch = enrollmentSystem.updateSearch.bind(enrollmentSystem);
window.addPlanClickEvents = enrollmentSystem.addPlanClickEvents.bind(enrollmentSystem);
window.standardSubmit = enrollmentSystem.handleSubmit.bind(enrollmentSystem);
